import type { PlanNode } from '@timelog/pm-shared';

import {
  BAR_INSET_PX,
  EMPTY_WINDOW_AFTER_DAYS,
  EMPTY_WINDOW_BEFORE_DAYS,
  WINDOW_PAD_DAYS,
} from './timelineGeometry';

// Direct module imports: this file must stay loadable in the node-environment
// unit tests, and the lib barrel is fine but the editor barrel is not — keep the
// habit consistent here.
import { epochDayToIso, isoToEpochDay } from '@/lib/epochDay';
import { formatMonthYear } from '@/lib/format';

/**
 * Everything the timeline needs to draw and edit itself, derived from the plan.
 *
 * All of it is pure: plan in, plain numbers out. The React components own no
 * date logic, and a drag reads its bounds from here once on pointerdown rather
 * than recomputing per frame.
 *
 * Offsets throughout are day columns relative to the window's first day, and
 * inclusive at both ends — a task from the 2nd to the 13th spans 12 columns.
 */

export type TimelineMonth = {
  key: string;
  label: string;
  startOffset: number;
  dayCount: number;
};

export type TimelineWindow = {
  /** Epoch day of column 0. */
  startDay: number;
  dayCount: number;
  /** null when today falls outside the window — see deriveTimelineWindow. */
  todayOffset: number | null;
  months: readonly TimelineMonth[];
};

export type TimelineSpan = { startOffset: number; endOffset: number };

export type ScheduledNode = { id: string; startOffset: number; endOffset: number };

export type TimelineModel = {
  window: TimelineWindow;
  /**
   * The bar to draw: a node's own dates unioned with every descendant's.
   * null when neither the node nor anything beneath it is scheduled.
   */
  spanById: ReadonlyMap<string, TimelineSpan | null>;
  /**
   * The node's OWN dates, ignoring descendants: the stored pair, or the default
   * a dateless node with a target gets (today → target, ADR 0050). null when it
   * has neither.
   */
  ownSpanById: ReadonlyMap<string, TimelineSpan | null>;
  /**
   * True when the bar is entirely a default — nothing in the node's subtree has
   * stored dates, so every day of it comes from today and a target date. Drawn
   * dashed: it is a suggestion, and the first drag on it writes real dates.
   */
  provisionalById: ReadonlyMap<string, boolean>;
  /**
   * The extent of everything BENEATH the node, ignoring its own dates. null on a
   * leaf and on a parent with nothing scheduled under it. This is the floor a
   * parent's bar cannot be shrunk past (ADR 0020), and the band drawn inside it.
   */
  descendantSpanById: ReadonlyMap<string, TimelineSpan | null>;
  isParentById: ReadonlyMap<string, boolean>;
  /**
   * Where the node's own `targetDate` falls in the window, as a day offset.
   * null when it has none, or when it falls outside the window entirely.
   *
   * Deliberately NOT derived from the bar. `targetDate` is the deadline signal,
   * independent of `endDate`, so the mark can land anywhere relative to the bar
   * — which is the whole comparison it exists to make: did the work I placed in
   * time land inside the deadline I already committed to?
   */
  targetOffsetById: ReadonlyMap<string, number | null>;
  /**
   * Every node that has dates of its own, in pre-order, so a subtree occupies a
   * contiguous slice. This is what a group drag shifts — defaults included, so
   * moving a group writes real dates for the children it visibly carried.
   */
  scheduledNodes: readonly ScheduledNode[];
  /** [from, to) into scheduledNodes — a group drag's payload without copying. */
  sliceById: ReadonlyMap<string, { from: number; to: number }>;
  /** Leaf tasks with no bar at all: the "click a track to schedule" affordance. */
  unscheduledLeafCount: number;
  /** Leaf tasks drawn only from the today → target default, not yet placed. */
  provisionalLeafCount: number;
};

type AbsoluteRange = { minDay: number; maxDay: number };

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

/** First day of the month containing `day`, as an epoch day. */
const startOfMonth = (day: number): number =>
  isoToEpochDay(`${epochDayToIso(day).slice(0, 7)}-01`)!;

/** Last day of the month containing `day`, as an epoch day. */
const endOfMonth = (day: number): number => {
  const iso = epochDayToIso(day);
  const year = Number(iso.slice(0, 4));
  const month = Number(iso.slice(5, 7));
  // Day 0 of the following month (month is 1-based, so it is the next index).
  return Date.UTC(year, month, 0) / 86_400_000;
};

const monthsBetween = (startDay: number, dayCount: number): TimelineMonth[] => {
  const months: TimelineMonth[] = [];
  let offset = 0;
  while (offset < dayCount) {
    const day = startDay + offset;
    const monthEnd = endOfMonth(day);
    const span = Math.min(monthEnd - day + 1, dayCount - offset);
    const iso = epochDayToIso(day);
    months.push({
      key: iso.slice(0, 7),
      label: formatMonthYear(iso),
      startOffset: offset,
      dayCount: span,
    });
    offset += span;
  }
  return months;
};

const buildWindow = (firstDay: number, lastDay: number, todayDay: number): TimelineWindow => {
  const startDay = startOfMonth(firstDay);
  const endDay = endOfMonth(lastDay);
  const dayCount = endDay - startDay + 1;
  const rawTodayOffset = todayDay - startDay;
  return {
    startDay,
    dayCount,
    /**
     * Deliberately null rather than clamped when today is outside. Stretching the
     * window to reach today would give a plan that finished in 2024 two years of
     * empty grid; a missing today line is honest, a pinned one lies about where
     * "now" is.
     */
    todayOffset: rawTodayOffset >= 0 && rawTodayOffset < dayCount ? rawTodayOffset : null,
    months: monthsBetween(startDay, dayCount),
  };
};

/**
 * The visible date range. Padded by a month each side so a bar can be dragged
 * past the plan's current extent, then snapped to whole months so the header band
 * never opens or closes mid-column.
 *
 * `bounds === null` (nothing scheduled at all) anchors on today instead, because
 * a dateless plan still needs a grid to click on.
 */
export const deriveTimelineWindow = (
  bounds: AbsoluteRange | null,
  todayDay: number,
): TimelineWindow =>
  bounds === null
    ? buildWindow(todayDay - EMPTY_WINDOW_BEFORE_DAYS, todayDay + EMPTY_WINDOW_AFTER_DAYS, todayDay)
    : buildWindow(bounds.minDay - WINDOW_PAD_DAYS, bounds.maxDay + WINDOW_PAD_DAYS, todayDay);

/**
 * The wider of two windows.
 *
 * The window only ever grows while the view is mounted. Recomputing it from the
 * plan after every drag would shift every offset the moment a bar crossed the
 * padding, and the horizontal scroll position would jump under the pointer
 * mid-drag.
 */
export const unionWindows = (a: TimelineWindow, b: TimelineWindow): TimelineWindow => {
  const startDay = Math.min(a.startDay, b.startDay);
  const endDay = Math.max(a.startDay + a.dayCount - 1, b.startDay + b.dayCount - 1);
  if (startDay === a.startDay && endDay === a.startDay + a.dayCount - 1) return a;
  if (startDay === b.startDay && endDay === b.startDay + b.dayCount - 1) return b;
  // Recover an absolute today from whichever window still contains it.
  const todayDay =
    a.todayOffset !== null
      ? a.startDay + a.todayOffset
      : b.todayOffset !== null
        ? b.startDay + b.todayOffset
        : // Neither contains today; any day outside the union keeps todayOffset null.
          startDay - 1;
  return buildWindow(startDay, endDay, todayDay);
};

/** A node's OWN stored range, or null when either date is missing. */
const ownRangeOf = (node: PlanNode): AbsoluteRange | null => {
  const startDay = isoToEpochDay(node.startDate);
  const endDay = isoToEpochDay(node.endDate);
  if (startDay === null || endDay === null) return null;
  // An end before its start draws as a single day rather than backwards.
  return { minDay: startDay, maxDay: Math.max(endDay, startDay) };
};

/**
 * The bar a node gets before it has been placed in time: today → its target
 * date (ADR 0050). The planning order is list first — names, hours, a target —
 * then the timeline, and a timeline of empty tracks did not look like the plan
 * just made. Derived, never stored: today moves, and a default written on view
 * would claim a decision nobody made.
 *
 * Only when NEITHER date is stored, so a half-typed pair is never papered over.
 * A target already past has no forward span to draw; its mark still shows.
 */
const defaultRangeOf = (node: PlanNode, todayDay: number): AbsoluteRange | null => {
  if (node.startDate !== undefined || node.endDate !== undefined) return null;
  const targetDay = isoToEpochDay(node.targetDate);
  if (targetDay === null || targetDay < todayDay) return null;
  return { minDay: todayDay, maxDay: targetDay };
};

/**
 * The same default as ISO dates, for surfaces outside the timeline — the side
 * panel shows it greyed in its start and end fields, so the panel and the
 * dashed bar describe the task the same way. null when there is no default.
 */
export const defaultScheduleOf = (
  node: PlanNode,
  todayIsoDate: string,
): { startDate: string; endDate: string } | null => {
  const todayDay = isoToEpochDay(todayIsoDate);
  if (todayDay === null) return null;
  const range = defaultRangeOf(node, todayDay);
  return range === null
    ? null
    : { startDate: epochDayToIso(range.minDay), endDate: epochDayToIso(range.maxDay) };
};

const union = (a: AbsoluteRange | null, b: AbsoluteRange | null): AbsoluteRange | null => {
  if (a === null) return b === null ? null : { ...b };
  if (b === null) return { ...a };
  return { minDay: Math.min(a.minDay, b.minDay), maxDay: Math.max(a.maxDay, b.maxDay) };
};

/**
 * Two iterative passes over the whole plan (BR-3: never recurse).
 *
 * The whole plan, not the visible rows: a folded parent must still cover the
 * children it is hiding, or its bar would change when collapsed.
 *
 * A node's bar is the UNION of its own dates and its descendants', not a pure
 * roll-up of the children. Project Ship shows why: "Backend implementation" runs
 * 30 Jun – 15 Aug and contains "API integration" (7 Jul – 1 Aug) as a subtask.
 * Rolling up from children alone would shrink that bar to the subtask and hide
 * three weeks of real work. Union also happens to reproduce the fixture's stored
 * parent dates exactly, on every parent — which is the evidence that it is the
 * rule the plan model already assumes.
 */
export const buildTimelineModel = (
  plan: readonly PlanNode[],
  todayIsoDate: string,
  previousWindow?: TimelineWindow,
): TimelineModel => {
  const todayDay = isoToEpochDay(todayIsoDate) ?? 0;
  const isParentById = new Map<string, boolean>();
  const provisionalById = new Map<string, boolean>();
  const targetDayById = new Map<string, number | null>();
  const ownById = new Map<string, AbsoluteRange | null>();
  const descendantById = new Map<string, AbsoluteRange | null>();
  const unionById = new Map<string, AbsoluteRange | null>();
  let unscheduledLeafCount = 0;
  let provisionalLeafCount = 0;
  let planBounds: AbsoluteRange | null = null;

  // Pass 1, post-order: own range, union range, and the plan's overall extent.
  type Frame = {
    node: PlanNode;
    childIndex: number;
    descendants: AbsoluteRange | null;
    /** Whether anything beneath has STORED dates, as opposed to defaults. */
    descendantsStored: boolean;
  };
  const frameOf = (node: PlanNode): Frame => ({
    node,
    childIndex: 0,
    descendants: null,
    descendantsStored: false,
  });

  for (const root of plan) {
    const stack: Frame[] = [frameOf(root)];

    while (stack.length > 0) {
      const frame = stack[stack.length - 1]!;

      if (frame.childIndex < frame.node.children.length) {
        const child = frame.node.children[frame.childIndex]!;
        frame.childIndex += 1;
        stack.push(frameOf(child));
        continue;
      }

      stack.pop();
      const node = frame.node;
      const isParent = node.children.length > 0;
      isParentById.set(node.id, isParent);

      const stored = ownRangeOf(node);
      const own = stored ?? defaultRangeOf(node, todayDay);
      targetDayById.set(node.id, isoToEpochDay(node.targetDate));
      if (!isParent && own === null) unscheduledLeafCount += 1;
      if (!isParent && stored === null && own !== null) provisionalLeafCount += 1;

      const combined = union(own, frame.descendants);
      const subtreeStored = stored !== null || frame.descendantsStored;
      provisionalById.set(node.id, combined !== null && !subtreeStored);
      ownById.set(node.id, own);
      // A leaf's frame is never assigned descendants, so it lands here as null —
      // which is exactly the "no floor, behave as before" contract.
      descendantById.set(node.id, frame.descendants);
      unionById.set(node.id, combined);
      planBounds = union(planBounds, combined);

      const parentFrame = stack[stack.length - 1];
      if (parentFrame) {
        parentFrame.descendants = union(parentFrame.descendants, combined);
        parentFrame.descendantsStored ||= subtreeStored;
      }
    }
  }

  const derived = deriveTimelineWindow(planBounds, todayDay);
  const window = previousWindow ? unionWindows(previousWindow, derived) : derived;

  const toSpan = (range: AbsoluteRange | null): TimelineSpan | null =>
    range === null
      ? null
      : {
          startOffset: range.minDay - window.startDay,
          endOffset: range.maxDay - window.startDay,
        };

  const spanById = new Map<string, TimelineSpan | null>();
  for (const [id, range] of unionById) spanById.set(id, toSpan(range));
  const ownSpanById = new Map<string, TimelineSpan | null>();
  for (const [id, range] of ownById) ownSpanById.set(id, toSpan(range));
  const descendantSpanById = new Map<string, TimelineSpan | null>();
  for (const [id, range] of descendantById) descendantSpanById.set(id, toSpan(range));

  // A target outside the window has no place to be drawn, so it reads as absent
  // rather than as an offset the caller must bounds-check.
  const targetOffsetById = new Map<string, number | null>();
  for (const [id, day] of targetDayById) {
    const offset = day === null ? null : day - window.startDay;
    targetOffsetById.set(
      id,
      offset === null || offset < 0 || offset >= window.dayCount ? null : offset,
    );
  }

  // Pass 2, pre-order: the drag payload. Every node with dates of its own, in
  // reading order, so each subtree is a contiguous slice — two integers per node
  // instead of a descendant array per node, which would be O(n²) on a deep chain.
  const scheduledNodes: ScheduledNode[] = [];
  const sliceById = new Map<string, { from: number; to: number }>();

  type WalkFrame = { node: PlanNode; childIndex: number; from: number };
  for (const root of plan) {
    const stack: WalkFrame[] = [{ node: root, childIndex: 0, from: scheduledNodes.length }];
    const own = ownSpanById.get(root.id);
    if (own) scheduledNodes.push({ id: root.id, ...own });

    while (stack.length > 0) {
      const frame = stack[stack.length - 1]!;
      if (frame.childIndex < frame.node.children.length) {
        const child = frame.node.children[frame.childIndex]!;
        frame.childIndex += 1;
        const childFrom = scheduledNodes.length;
        const childOwn = ownSpanById.get(child.id);
        if (childOwn) scheduledNodes.push({ id: child.id, ...childOwn });
        stack.push({ node: child, childIndex: 0, from: childFrom });
        continue;
      }
      stack.pop();
      sliceById.set(frame.node.id, { from: frame.from, to: scheduledNodes.length });
    }
  }

  return {
    window,
    spanById,
    ownSpanById,
    provisionalById,
    descendantSpanById,
    isParentById,
    targetOffsetById,
    scheduledNodes,
    sliceById,
    unscheduledLeafCount,
    provisionalLeafCount,
  };
};

/**
 * Bar position in pixels at the given zoom, inset so neighbouring bars do not
 * touch. The inset scales down with the day width so it cannot eat a whole day
 * column when zoomed far out (at the default 7px it is the full BAR_INSET_PX).
 */
export const barGeometry = (
  span: TimelineSpan,
  dayWidth: number,
): { left: number; width: number } => {
  const inset = Math.min(BAR_INSET_PX, dayWidth / 2);
  return {
    left: span.startOffset * dayWidth,
    width: Math.max(dayWidth - inset, (span.endOffset - span.startOffset + 1) * dayWidth - inset),
  };
};

/** Which day column an x offset within a track falls on, at the given zoom. */
export const dayAtOffsetX = (
  x: number,
  dayWidth: number,
  dayCount: number,
  lastAllowed?: number,
): number =>
  clamp(Math.round(x / dayWidth), 0, Math.min(dayCount - 1, lastAllowed ?? dayCount - 1));

/**
 * A dragged or resized span, clamped inside the window.
 *
 * `move` clamps the DELTA so the duration survives hitting the window edge.
 * Clamping the two ends independently (as the prototype does) silently shortens
 * a bar dragged into the wall, which loses a date the user never edited.
 */
export const resizeSpan = (
  span: TimelineSpan,
  mode: 'move' | 'start' | 'end',
  deltaDays: number,
  dayCount: number,
  /**
   * A span the result must still CONTAIN — a parent's descendants, which it
   * cannot shrink past (ADR 0020). Composes safely with the mutual pin and the
   * window clamp because the bar IS the union of own and descendants, so
   * `descendants ⊆ span` by construction. `move` ignores it: a group drag
   * carries the descendants along.
   */
  descendants?: TimelineSpan | null,
): TimelineSpan => {
  if (mode === 'move') {
    const delta = clamp(deltaDays, -span.startOffset, dayCount - 1 - span.endOffset);
    return { startOffset: span.startOffset + delta, endOffset: span.endOffset + delta };
  }
  if (mode === 'start') {
    // Dragging the start past the end pins it to the end: a one-day task.
    // `?? Infinity`, not `||`: a descendant starting at offset 0 must stay 0.
    return {
      startOffset: clamp(
        Math.min(
          span.startOffset + deltaDays,
          span.endOffset,
          descendants?.startOffset ?? Number.POSITIVE_INFINITY,
        ),
        0,
        dayCount - 1,
      ),
      endOffset: span.endOffset,
    };
  }
  return {
    startOffset: span.startOffset,
    endOffset: clamp(
      Math.max(
        span.endOffset + deltaDays,
        span.startOffset,
        descendants?.endOffset ?? Number.NEGATIVE_INFINITY,
      ),
      0,
      dayCount - 1,
    ),
  };
};

/**
 * The node's new OWN span after dragging one edge of its (possibly unioned) bar.
 *
 * Seeded from the VISIBLE bar, so what you grab is what moves — a parent whose
 * own dates sit inside its children's has visible edges that are the children's,
 * and seeding from `own` would leave a dead zone before anything happened.
 *
 * But only the DRAGGED edge is written through. Storing the whole resized bar
 * would silently widen the untouched edge to the children's extent — the very
 * data ADR 0018 protects, inverted — and would make the bar permanently
 * sticky-wide, since `own` would have swallowed the descendants and could no
 * longer follow them back when a child moves inward.
 *
 * A node with no dates of its own has no edge to keep, so it takes both from the
 * bar: that is how a pure roll-up parent gains its own dates. Both must be
 * written, or `ownRangeOf` reads null and the edit vanishes.
 */
export const resizeOwnSpan = (
  bar: TimelineSpan,
  own: TimelineSpan | null,
  descendants: TimelineSpan | null,
  mode: 'start' | 'end',
  deltaDays: number,
  dayCount: number,
): TimelineSpan => {
  const resized = resizeSpan(bar, mode, deltaDays, dayCount, descendants);
  if (own === null) return resized;

  if (mode === 'start') {
    const floor = descendants?.startOffset ?? Number.POSITIVE_INFINITY;
    /*
      Below the floor the parent's own edge IS the visible one, so it takes the
      new position. Pinned AT the floor, an own start that is already later is
      invisible and stays put — a blocked drag must write nothing.
    */
    const startOffset =
      resized.startOffset < floor
        ? resized.startOffset
        : Math.max(own.startOffset, resized.startOffset);
    // Pin rather than invert, matching resizeSpan, for a parent scheduled
    // entirely after its children.
    return { startOffset, endOffset: Math.max(own.endOffset, startOffset) };
  }

  const floor = descendants?.endOffset ?? Number.NEGATIVE_INFINITY;
  const endOffset =
    resized.endOffset > floor ? resized.endOffset : Math.min(own.endOffset, resized.endOffset);
  return { startOffset: Math.min(own.startOffset, endOffset), endOffset };
};

/** How far a group can shift together before one member hits a window edge. */
export const groupShiftBounds = (
  nodes: readonly ScheduledNode[],
  dayCount: number,
): { minDelta: number; maxDelta: number } => {
  if (nodes.length === 0) return { minDelta: 0, maxDelta: 0 };
  let earliest = nodes[0]!.startOffset;
  let latest = nodes[0]!.endOffset;
  for (const node of nodes) {
    if (node.startOffset < earliest) earliest = node.startOffset;
    if (node.endOffset > latest) latest = node.endOffset;
  }
  // `0 - earliest` rather than `-earliest`: negative zero is not equal to zero
  // under Object.is, which trips both assertions and identity checks.
  return { minDelta: 0 - earliest, maxDelta: dayCount - 1 - latest };
};

/** A span back to the stored ISO date pair. */
export const spanToIsoPatch = (
  window: TimelineWindow,
  span: TimelineSpan,
): { startDate: string; endDate: string } => ({
  startDate: epochDayToIso(window.startDay + span.startOffset),
  endDate: epochDayToIso(window.startDay + span.endOffset),
});

/** A single window-relative day offset back to an ISO date. */
export const offsetToIso = (window: TimelineWindow, offset: number): string =>
  epochDayToIso(window.startDay + offset);
